import { type FC, useEffect, useState } from "react";
import {
  chooseSalaryUnit,
  omitEmploymentName,
  formatSalaryPriceRound,
} from "@root/userFrontend/utils/formatSalary";
import { useInView } from "react-intersection-observer";
import { useFixBackground } from "@root/shared/hooks/useFixBackground";
import { useTransitionWithQuery } from "@userFrontend/features/publication/hooks/useTransitionWithQuery";
import { useExtendScoutDeadlineButton } from "@userFrontend/hooks/useExtendScoutDeadlineButton";
import { pushDataLayer } from "@root/shared/utils/pushDataLayer";
import { useSecondarySearchHitCassettes } from "./useSecondarySearchHitCassettes";
import { type ScoutCassetteProps } from "./type";
import { Presentation } from "./Presentation";
import { GA_SCOUT_SHOW_MODAL_BUTTON_IN_VIEW, GA_SCOUT_SHOW_MODAL_BUTTON } from "./constants";

export const ScoutCassette: FC<ScoutCassetteProps> = (props) => {
  const [isOpenModal, setIsOpenModal] = useState(false);
  const {
    isSp,
    workCode,
    salaries,
    fetchShopWorksPath,
    query,
    labels: { isDecline },
  } = props;
  const { shops, fetchWorks, isShowIndicator } = useSecondarySearchHitCassettes({
    url: fetchShopWorksPath,
    query,
    scoutedFlag: !isDecline,
  });
  const { fixBody, unFixBody } = useFixBackground();
  const { ref: indicatorRef, inView: indicatorInView } = useInView();
  const { ref: openModalButtonRef, inView: openModalButtonInView } = useInView();
  const { handleClickExtendScoutDeadlineButton } = useExtendScoutDeadlineButton();
  const { handleClickLink } = useTransitionWithQuery();

  const employmentAndSalaryType = salaries.map((salary) => ({
    employmentName: omitEmploymentName(salary.employmentName),
    salaryMax: formatSalaryPriceRound(salary.salaryMax),
    salaryMaxUnit: chooseSalaryUnit(salary.salaryMax),
    salaryMin: formatSalaryPriceRound(salary.salaryMin),
    salaryMinUnit: chooseSalaryUnit(salary.salaryMin),
    salaryTypeName: salary.salaryTypeName,
  }));

  const handleOpenModal = () => {
    setIsOpenModal(true);
    fixBody();
    pushDataLayer({
      event: GA_SCOUT_SHOW_MODAL_BUTTON,
      job_id: workCode,
    });

    if (shops.length) return;
    fetchWorks();
  };

  const handleCloseModal = () => {
    unFixBody();
    setIsOpenModal(false);
  };

  // 店舗ごと求人モーダルにて、追加の店舗ごと求人データを取得する
  // インジケーターが画面内に入ってきたら処理開始、全件取得したらインジケーターは消える
  useEffect(() => {
    if (indicatorInView) {
      fetchWorks();
    }
    // handleInviewを含めるとループしてしまうので除外する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indicatorInView]);

  // 店舗モーダルのimpressionイベントはフロント側で送る
  useEffect(() => {
    if (openModalButtonInView) {
      // イベント送信
      pushDataLayer({
        event: GA_SCOUT_SHOW_MODAL_BUTTON_IN_VIEW,
        job_id: workCode,
      });
    }
  }, [openModalButtonInView, workCode]);

  return (
    <Presentation
      {...props}
      isSp={isSp}
      employmentAndSalaryType={employmentAndSalaryType}
      isOpenModal={isOpenModal}
      handleOpenModal={handleOpenModal}
      handleCloseModal={handleCloseModal}
      handleClickLink={handleClickLink}
      handleClickExtendScoutDeadlineButton={handleClickExtendScoutDeadlineButton}
      cassettes={shops}
      isShowIndicator={isShowIndicator}
      openModalButtonRef={openModalButtonRef}
      indicatorRef={indicatorRef}
    />
  );
};
